import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Buildings, Users, FileText, Receipt, ChartLineUp, Package, Storefront, ArrowRight } from "@phosphor-icons/react";

const Stat = ({ label, value, icon: Icon, testid }) => (
  <Card className="p-5 rounded-sm border-border" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        <div className="font-display text-3xl font-black mt-2">{value ?? "—"}</div>
      </div>
      <div className="w-10 h-10 rounded-sm bg-[var(--uc-primary)]/10 flex items-center justify-center">
        <Icon size={20} weight="duotone" className="text-[var(--uc-primary)]" />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, q, l] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/quotes"),
          api.get("/leads"),
        ]);
        setStats(s.data || {});
        setRecentQuotes((q.data || []).slice(0, 5));
        setRecentLeads((l.data || []).slice(0, 5));
      } catch {}
    })();
  }, []);

  const role = user?.role;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Dashboard</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-1">Welcome back, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-sm text-muted-foreground mt-1">Here's what's happening across your partner account today.</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" className="rounded-sm" data-testid="dash-new-lead">
            <Link to="/leads"><ChartLineUp size={16} className="mr-1" /> New lead</Link>
          </Button>
          <Button asChild className="rounded-sm bg-[var(--uc-primary)] hover:bg-[var(--uc-primary-hover)] text-white" data-testid="dash-new-quote">
            <Link to="/quotes/new"><FileText size={16} className="mr-1" /> New quote</Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {role === "super_user" && (
          <>
            <Stat label="Users" value={stats.users} icon={Users} testid="stat-users" />
            <Stat label="Partners" value={stats.partners} icon={Buildings} testid="stat-partners" />
            <Stat label="Products" value={stats.products} icon={Package} testid="stat-products" />
            <Stat label="Leads" value={stats.leads} icon={ChartLineUp} testid="stat-leads" />
            <Stat label="Quotes" value={stats.quotes} icon={FileText} testid="stat-quotes" />
            <Stat label="Paid orders" value={stats.orders} icon={Receipt} testid="stat-orders" />
          </>
        )}
        {role === "partner_success_manager" && (
          <>
            <Stat label="Managed partners" value={stats.managed_partners} icon={Buildings} testid="stat-partners" />
            <Stat label="Leads" value={stats.leads} icon={ChartLineUp} testid="stat-leads" />
            <Stat label="Quotes" value={stats.quotes} icon={FileText} testid="stat-quotes" />
            <Stat label="Paid orders" value={stats.orders} icon={Receipt} testid="stat-orders" />
          </>
        )}
        {(role === "partner_manager" || role === "partner_user") && (
          <>
            <Stat label="Team members" value={stats.team_members} icon={Users} testid="stat-users" />
            <Stat label="My leads" value={stats.leads} icon={ChartLineUp} testid="stat-leads" />
            <Stat label="My quotes" value={stats.quotes} icon={FileText} testid="stat-quotes" />
            <Stat label="My orders" value={stats.orders} icon={Receipt} testid="stat-orders" />
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 rounded-sm border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg">Recent quotes</h3>
            <Link to="/quotes" className="text-xs uppercase tracking-[0.2em] text-[var(--uc-primary)]" data-testid="quotes-link">View all</Link>
          </div>
          <div className="space-y-2">
            {recentQuotes.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No quotes yet.</div>}
            {recentQuotes.map((q) => (
              <div key={q.quote_id} className="flex items-center justify-between border-t border-border py-3 first:border-t-0" data-testid={`quote-row-${q.quote_id}`}>
                <div>
                  <div className="text-sm font-medium">{q.quote_number}</div>
                  <div className="text-xs text-muted-foreground">{q.customer_name} · {q.customer_company || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold">${q.total?.toFixed(2)}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{q.status}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 rounded-sm border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg">Recent leads</h3>
            <Link to="/leads" className="text-xs uppercase tracking-[0.2em] text-[var(--uc-primary)]" data-testid="leads-link">View all</Link>
          </div>
          <div className="space-y-2">
            {recentLeads.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No leads yet.</div>}
            {recentLeads.map((l) => (
              <div key={l.lead_id} className="flex items-center justify-between border-t border-border py-3 first:border-t-0" data-testid={`lead-row-${l.lead_id}`}>
                <div>
                  <div className="text-sm font-medium">{l.company_name}</div>
                  <div className="text-xs text-muted-foreground">{l.contact_name} · {l.interest}</div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{l.status}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-6 rounded-sm border-border bg-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-display font-bold text-lg">Shop the catalog</h3>
            <p className="text-sm text-muted-foreground">Order hardware, SIP trunks, hosted PBX and more — paid via Stripe or send as a quote.</p>
          </div>
          <Button asChild className="rounded-sm bg-[var(--uc-primary)] hover:bg-[var(--uc-primary-hover)] text-white" data-testid="dash-shop-cta">
            <Link to="/store"><Storefront size={16} className="mr-2" /> Open store <ArrowRight size={14} className="ml-1" /></Link>
          </Button>
        </div>
      </Card>
    </div>
  );
}
